import React, { useState, useEffect } from 'react';
import { IcoMail } from '../common/Icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI, shopifyAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '', company: '', website: '', order_volume: '' });
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [disabledMsg, setDisabledMsg] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const { login, verifyRegister } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const startResendCooldown = () => setResendIn(45);

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.trim().length !== 6) return toast.error('Enter the 6-digit code');
    setOtpLoading(true);
    try {
      await verifyRegister(otpEmail, otpCode.trim()); // creates account + auto-logs in
      setShowOtp(false);
      navigate('/onboarding'); // straight to the business-details step
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendIn > 0) return;
    try {
      await authAPI.registerResend({ email: otpEmail });
      toast.success('A new code has been sent');
      startResendCooldown();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resend the code');
    }
  };

  useEffect(() => {
    if (new URLSearchParams(location.search).get('disabled') === '1') {
      setDisabledMsg('Your account has been disabled. Please contact support to reactivate.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPendingMsg('');
    setSuccessMsg('');
    setDisabledMsg('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        // If the user arrived from a Shopify App Store install, link the pending store now
        const pendingInstall = new URLSearchParams(location.search).get('pending_install');
        if (pendingInstall) {
          try {
            const res = await shopifyAPI.claimInstall(pendingInstall);
            toast.success(`Connected ${res.data?.store?.store_name || 'your Shopify store'}`);
            navigate('/dashboard/stores');
            return;
          } catch (claimErr) {
            toast.error(claimErr.response?.data?.error || 'Could not connect the Shopify store. Try connecting it manually.');
            navigate('/dashboard/stores');
            return;
          }
        }
        navigate('/dashboard');
      } else {
        const name = form.name.trim();
        const email = form.email.trim();
        const mobile = form.mobile.replace(/\D/g, '');
        const password = form.password;
        const company = form.company.trim();
        const website = form.website.trim();
        const order_volume = form.order_volume;
        if (!name || !email || !mobile || !password || !company || !website) {
          toast.error('Please fill all required fields');
          setLoading(false);
          return;
        }
        if (!/^\S+@\S+\.\S+$/.test(email)) {
          toast.error('Enter a valid email address');
          setLoading(false);
          return;
        }
        if (!/^[6-9]\d{9}$/.test(mobile)) {
          toast.error('Enter a valid 10-digit mobile number');
          setLoading(false);
          return;
        }
        if (!order_volume) {
          toast.error('Select your monthly order volume');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        await authAPI.registerInit({ name, email, mobile, password, company_name: company, website, order_volume });
        setOtpEmail(email);
        setOtpCode('');
        setShowOtp(true);
        startResendCooldown();
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.pending) {
        setPendingMsg(data.error);
      } else if (data?.disabled) {
        setDisabledMsg(data.error);
      } else {
        toast.error(data?.error || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const res = await authAPI.demo();
      localStorage.setItem('oms_token', res.data.token);
      if (res.data.dl_token) localStorage.setItem('oms_dl_token', res.data.dl_token);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error('Demo login failed');
    } finally {
      setDemoLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setPendingMsg('');
    setSuccessMsg('');
  };

  return (
    <div style={styles.page}>
      {/* Brand panel */}
      <div style={styles.brandPanel}>
        <div style={styles.brandGlow} />
        <div style={styles.brandInner}>
          <img src="/logo-color.png" alt="ModCommerce" style={{ height: 36, objectFit: 'contain', marginBottom: 32 }} />
          <img src="/landing/auth.jpg" alt="" style={styles.brandArt} />
          <h2 style={styles.brandTitle}>Manage all your orders from one place</h2>
          <p style={styles.brandSub}>Connect Shopify, sync orders in real time, track shipments and manage inventory — securely.</p>
        </div>
      </div>

      {/* Form panel */}
      <div style={styles.formPanel}>
        <div style={styles.card}>
          <img src="/logo-color.png" alt="ModCommerce" style={styles.mobileLogo} />
          <h1 style={styles.cardTitle}>{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
          <p style={styles.cardSub}>{mode === 'login' ? 'Log in to your ModCommerce dashboard' : 'Start managing your orders in minutes'}</p>

          {mode === 'register' && <SignupStepBar current={0} />}

          <div style={styles.tabs}>
            <button style={mode === 'login' ? styles.tabActive : styles.tab} onClick={() => switchMode('login')}>Login</button>
            <button style={mode === 'register' ? styles.tabActive : styles.tab} onClick={() => switchMode('register')}>Sign Up</button>
          </div>

          {successMsg && <div style={styles.successBanner}>{successMsg}</div>}
          {pendingMsg && <div style={styles.pendingBanner}>{pendingMsg}</div>}
          {disabledMsg && <div style={styles.disabledBanner}>{disabledMsg}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            {mode === 'register' && (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>Full Name *</label>
                  <input style={styles.input} type="text" placeholder="Your name" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Brand / Company Name *</label>
                  <input style={styles.input} type="text" placeholder="e.g. Acme Apparel" value={form.company}
                    onChange={e => setForm({ ...form, company: e.target.value })} required />
                </div>
              </>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Email {mode === 'register' && '*'}</label>
              <input style={styles.input} type="email" placeholder="you@example.com" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
            {mode === 'register' && (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>Mobile Number *</label>
                  <div style={styles.phoneWrap}>
                    <span style={styles.phonePrefix}>+91</span>
                    <input style={styles.phoneInput} type="tel" inputMode="numeric" maxLength={10}
                      placeholder="9876543210" value={form.mobile}
                      onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} required />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Website or Instagram *</label>
                  <input style={styles.input} type="text" placeholder="https://yourstore.com  or  @yourbrand" value={form.website}
                    onChange={e => setForm({ ...form, website: e.target.value })} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Orders you ship per month *</label>
                  <div style={styles.volRow}>
                    {['0-100', '100-500', '500-1000', '1000+'].map(o => (
                      <button type="button" key={o}
                        style={{ ...styles.volOpt, ...(form.order_volume === o ? styles.volOptOn : {}) }}
                        onClick={() => setForm({ ...form, order_volume: o })}>{o}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Password {mode === 'register' && '*'}</label>
              <input style={styles.input} type="password" placeholder="••••••••" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} required />
            </div>
            <button style={loading ? styles.btnDisabled : styles.btn} type="submit" disabled={loading}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Account'}
            </button>
            {mode === 'register' && (
              <p style={styles.consent}>
                By signing up, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>Terms &amp; Conditions</a>,{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>Privacy Policy</a>,{' '}
                <a href="/dpa" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>Data Processing Addendum</a>{' '}
                and all other policies.
              </p>
            )}
          </form>

          <p style={styles.backLink}>
            <a href="/" style={styles.backAnchor}>← Back to home</a>
          </p>
        </div>
      </div>

      {/* Email OTP verification modal */}
      {showOtp && (
        <div style={styles.otpBackdrop}>
          <div style={styles.otpModal}>
            <SignupStepBar current={1} />
            <div style={styles.otpIcon}><IcoMail size={28} /></div>
            <h2 style={styles.otpTitle}>Verify your email</h2>
            <p style={styles.otpSub}>We sent a 6-digit code to <strong style={{ color: '#0f172a' }}>{otpEmail}</strong>. Enter it below to finish creating your account.</p>
            <form onSubmit={verifyOtp} style={styles.otpForm}>
              <input
                style={styles.otpInput}
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                placeholder="------"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
              />
              <button style={otpLoading ? styles.btnDisabled : styles.btn} type="submit" disabled={otpLoading}>
                {otpLoading ? 'Verifying...' : 'Verify & Create Account'}
              </button>
              <p style={styles.consent}>
                By verifying, you confirm you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>Terms</a>,{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>Privacy Policy</a> and{' '}
                <a href="/dpa" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>DPA</a>.
              </p>
            </form>
            <div style={styles.otpFooter}>
              <button style={styles.otpResend} onClick={resendOtp} disabled={resendIn > 0}>
                {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
              </button>
              <button style={styles.otpCancel} onClick={() => setShowOtp(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SIGNUP_STEPS = ['Account', 'Verify email', 'Business details'];
export function SignupStepBar({ current }) {
  return (
    <div style={styles.stepBar}>
      {SIGNUP_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div style={styles.step}>
            <div style={{ ...styles.stepDot, ...(i <= current ? styles.stepDotOn : {}) }}>{i < current ? '✓' : i + 1}</div>
            <span style={{ ...styles.stepLbl, ...(i <= current ? styles.stepLblOn : {}) }}>{s}</span>
          </div>
          {i < SIGNUP_STEPS.length - 1 && <div style={{ ...styles.stepLine, ...(i < current ? styles.stepLineOn : {}) }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', display: 'flex', flexWrap: 'wrap', background: '#ffffff' },
  stepBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 20 },
  step: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 },
  stepDot: { width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 },
  stepDotOn: { background: '#2563eb', color: '#fff' },
  stepLbl: { fontSize: 10.5, color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' },
  stepLblOn: { color: '#2563eb' },
  stepLine: { flex: 1, height: 2, background: '#e2e8f0', maxWidth: 40, marginBottom: 16 },
  stepLineOn: { background: '#2563eb' },

  // Brand panel (left)
  brandPanel: { position: 'relative', overflow: 'hidden', flex: '1 1 460px', background: 'linear-gradient(160deg, #eef4ff 0%, #dbe9ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 40px' },
  brandGlow: { position: 'absolute', top: -140, right: -120, width: 460, height: 460, background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0) 70%)' },
  brandInner: { position: 'relative', maxWidth: 440, textAlign: 'center' },
  brandArt: { width: '100%', maxWidth: 360, borderRadius: 20, boxShadow: '0 30px 70px rgba(37,99,235,0.18)', marginBottom: 28 },
  brandTitle: { color: '#0f172a', fontSize: 26, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.02em', lineHeight: 1.25 },
  brandSub: { color: '#475569', fontSize: 15, lineHeight: 1.6, margin: 0 },

  // Form panel (right)
  formPanel: { flex: '1 1 460px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' },
  card: { background: '#ffffff', borderRadius: 16, padding: 8, width: '100%', maxWidth: 400 },
  mobileLogo: { height: 34, objectFit: 'contain', marginBottom: 20, display: 'none' },
  cardTitle: { color: '#0f172a', fontSize: 26, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.02em' },
  cardSub: { color: '#64748b', fontSize: 14, margin: '0 0 24px' },
  tabs: { display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 24 },
  tab: { flex: 1, padding: '8px 0', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', borderRadius: 6, fontSize: 14, fontWeight: 500 },
  tabActive: { flex: 1, padding: '8px 0', background: '#2563eb', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 6, fontSize: 14, fontWeight: 600 },
  successBanner: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', color: '#16a34a', fontSize: 13, marginBottom: 16 },
  pendingBanner: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', color: '#d97706', fontSize: 13, marginBottom: 16 },
  disabledBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#374151', fontSize: 13, fontWeight: 500 },
  input: { background: '#f8fafc', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 14px', color: '#0f172a', fontSize: 14, outline: 'none' },
  phoneWrap: { display: 'flex', alignItems: 'stretch', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', background: '#f8fafc' },
  phonePrefix: { display: 'flex', alignItems: 'center', padding: '0 12px', background: '#eef2f7', color: '#475569', fontSize: 14, fontWeight: 600, borderRight: '1px solid #d1d5db' },
  phoneInput: { flex: 1, background: 'transparent', border: 'none', padding: '10px 14px', color: '#0f172a', fontSize: 14, outline: 'none', width: '100%' },
  volRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 },
  volOpt: { background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '9px 0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  volOptOn: { background: '#eff6ff', borderColor: '#2563eb', color: '#2563eb' },
  btn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8, boxShadow: '0 1px 3px rgba(37,99,235,0.3)' },
  btnDisabled: { background: '#e2e8f0', color: '#94a3b8', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: 'not-allowed', marginTop: 8 },
  divider: { display: 'flex', alignItems: 'center', margin: '20px 0 0', gap: 12 },
  dividerText: { color: '#94a3b8', fontSize: 13, background: '#ffffff', padding: '0 8px' },
  demoBtn: { width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '11px 0', fontSize: 14, cursor: 'pointer', fontWeight: 500 },
  demoBtnDisabled: { width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#94a3b8', borderRadius: 8, padding: '11px 0', fontSize: 14, cursor: 'not-allowed' },
  demoNote: { textAlign: 'center', color: '#94a3b8', fontSize: 11, marginTop: 6 },
  backLink: { textAlign: 'center', marginTop: 20 },
  backAnchor: { color: '#94a3b8', fontSize: 12, textDecoration: 'none' },
  consent: { color: '#94a3b8', fontSize: 11.5, lineHeight: 1.6, textAlign: 'center', margin: '4px 2px 0' },
  consentLink: { color: '#2563eb', textDecoration: 'none', fontWeight: 600 },

  // OTP modal
  otpBackdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  otpModal: { background: '#fff', borderRadius: 18, padding: '32px 32px 24px', width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: 'var(--mc-e4)' },
  otpIcon: { width: 60, height: 60, margin: '0 auto 16px', borderRadius: 16, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 },
  otpTitle: { color: '#0f172a', fontSize: 21, fontWeight: 800, margin: '0 0 8px' },
  otpSub: { color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: '0 0 22px' },
  otpForm: { display: 'flex', flexDirection: 'column' },
  otpInput: { width: '100%', boxSizing: 'border-box', background: '#f8fafc', border: '1px solid #d1d5db', borderRadius: 12, padding: '14px 0', color: '#0f172a', fontSize: 28, fontWeight: 700, letterSpacing: 12, textAlign: 'center', outline: 'none', marginBottom: 16 },
  otpFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 },
  otpResend: { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 4 },
  otpCancel: { background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', padding: 4 },
};
